import * as React from 'react';
import {Query, Mutation} from 'react-apollo';
import {connect} from 'react-redux';

import {Assignables} from 'nutshell-core/assignables';
import * as Leads from 'nutshell-core/leads';
import * as Schema from 'nutshell-core/schema';
import type {Assignable} from 'nutshell-core/types';

import type {
    GetUserAssignmentsForStage as GetUserAssignmentsForStageQuery,
    GetUserAssignmentsForStageVariables as GetUserAssignmentsForStageQueryVariables,
    SetStageLeadAssignmentAutomation as SetStageLeadAssignmentAutomationMutation,
    SetStageLeadAssignmentAutomationVariables as SetStageLeadAssignmentAutomationMutationVariables,
    StageLeadAssignmentAutomation as StageLeadAssignmentAutomationFragment,
    EntityFilterFieldConfigInput,
    SetStageLeadAssignmentAutomationInputFieldAutomationCase,
} from 'nutshell-graphql-types';

import type {NutshellState} from '../../../../store';

import {refetchSalesAutomationOpenLeadQueries} from '../../app/sales-automation-open-leads';
import SET_USER_ASSIGNMENT from '../../graphql/mutations/set-user-assignment.graphql';
import GET_USER_ASSIGNMENTS_FOR_STAGE from '../../graphql/queries/get-user-assignments-for-stage.graphql';

import {FormUserAssignmentsComponent} from './form-user-assignments-component';
import type {UserAssignmentsFormData} from './form-user-assignments-component';
import type {UserAssignmentAutomation} from './user-assignments-types';

type OwnProps = {
    entityId: string | null | undefined;
    parentId: string;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
};

// -----------------------------------------------------------------------------
//   React-redux connector
// -----------------------------------------------------------------------------

type StateProps = {
    assignables: Assignable[];
    schema: Schema.Properties;
};

type ConnectedProps = OwnProps & StateProps;

function mapStateToProps(state: NutshellState): StateProps {
    return {
        // @ts-expect-error
        assignables: Assignables.getAllEnabled(state),
        schema: Leads.getLeadsSchemaPropertyMap(state),
    };
}

// @ts-expect-error
const connector = connect<ConnectedProps, OwnProps, _, _, _, _>(mapStateToProps);

// Transforms autoamtions from the server into objects
// we can work with more easily on the client (smell - server should be helping
// us!)
function cleanSpecialCases(
    automations: StageLeadAssignmentAutomationFragment[]
): UserAssignmentAutomation[] {
    return automations.map((automation) => ({
        assignee: automation.assignee,
        shouldUnassign: automation.shouldUnassign,
        shouldRoundRobin: automation.shouldRoundRobin,
        criteria: getCriteriaFromAutomation(automation),
    }));
}

/*
 * Extract the config object from a user assignment automation, if it exists
 */
function getCriteriaFromAutomation(automation: StageLeadAssignmentAutomationFragment) {
    if (!automation.predicate.conditions[0]) {
        // This shouldn't happen, but have to check it for flow typing
        throw new Error('getCriteriaFromAutomation: Automation recieved without any conditions');
    }

    if (automation.predicate.conditions[0].config.__typename === 'ConditionConfigEntityFilter') {
        const criteria = automation.predicate.conditions[0].config.filter;
        if (
            criteria.__typename === 'EntityFilterFieldConfigCurrency' ||
            criteria.__typename === 'EntityFilterFieldConfigRelationship'
        ) {
            return criteria;
        }
    }

    // This shouldn't happen, but we have to guarantee this function returns a criteria.
    throw new Error('getCriteriaFromAutomation: Got a criteria of an incorrect type');
}

// -----------------------------------------------------------------------------
//   SetUserAssignments mutation
// -----------------------------------------------------------------------------

function getDefaultConfig(formData: UserAssignmentsFormData) {
    if (formData.defaultAutomation && formData.defaultAutomation.shouldUnassign)
        return {shouldUnassign: true};

    if (formData.defaultAutomation && formData.defaultAutomation.assignee) {
        return {
            assigneeId: formData.defaultAutomation.assignee.id,
            shouldRoundRobin: formData.defaultAutomation.shouldRoundRobin,
        };
    }

    return null;
}

function getAutomationCases(
    formData: UserAssignmentsFormData
): SetStageLeadAssignmentAutomationInputFieldAutomationCase[] {
    // @ts-expect-error
    return formData.specialCases
        .map((specialCase) => {
            return specialCase.criteria
                ? {
                      assigneeId: specialCase.assignee ? specialCase.assignee.id : null,
                      shouldUnassign: specialCase.shouldUnassign,
                      shouldRoundRobin: specialCase.shouldRoundRobin,
                      condition: {
                          filter: transformCriteriaToInputConfig(specialCase.criteria),
                      },
                  }
                : // This weird `null` + filter(Boolean) is the only way I could
                  // figure out how to satisfy flow in knowing that we're only
                  // mapping specialCases that have a criteria property.
                  null;
        })
        .filter(Boolean);
}

// Our input objects are nested under a `config` property, so we need
// to do that translation here.
//
// This is an exhaustive list of all different config types to satisfy flow,
// but really we're only dealing with CURRENCY and RELATIONSHIP configs
// @ts-expect-error
function transformCriteriaToInputConfig(criteria): EntityFilterFieldConfigInput {
    switch (criteria.__typename) {
        case 'EntityFilterFieldConfigCurrency': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
                currencyConfig: {
                    currencyValue: criteria.currencyValue,
                    currencyComparator: criteria.currencyComparator,
                    currencyCode: criteria.currencyCode,
                },
            };
        }
        case 'EntityFilterFieldConfigRelationship': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
                relationshipConfig: {
                    relationshipData: criteria.relationshipData,
                    relationshipAnyAll: criteria.relationshipAnyAll,
                },
            };
        }
        case 'EntityFilterFieldConfigIsSet': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
                isSetConfig: {
                    isSetValue: criteria.isSetValue,
                },
            };
        }
        case 'EntityFilterFieldConfigBoolean': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
            };
        }
        case 'EntityFilterFieldConfigEnum': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
                enumConfig: {
                    enumValue: criteria.enumValue,
                    enumIsNot: false,
                },
            };
        }
        case 'EntityFilterFieldConfigInteger': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
            };
        }
        case 'EntityFilterFieldConfigPhone': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
            };
        }
        case 'EntityFilterFieldConfigString': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
                stringConfig: {
                    stringContains: criteria.stringContains,
                },
            };
        }
        case 'EntityFilterFieldConfigStrings': {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
            };
        }
        default: {
            return {
                fieldName: criteria.fieldName,
                fieldType: criteria.fieldType,
            };
        }
    }
}

class UserAssignmentsQuery extends Query<
    GetUserAssignmentsForStageQuery,
    GetUserAssignmentsForStageQueryVariables
> {}

class SetUserAssignmentsMutation extends Mutation<
    SetStageLeadAssignmentAutomationMutation,
    SetStageLeadAssignmentAutomationMutationVariables
> {}

const FormUserAssignmentsGraphQL = (props: ConnectedProps) => (
    <UserAssignmentsQuery
        query={GET_USER_ASSIGNMENTS_FOR_STAGE}
        variables={{stageId: props.parentId}}
    >
        {({data}) => {
            const stage = data ? data.stage : null;

            const initialValues = stage
                ? {
                      defaultAutomation:
                          stage.editableStageLeadAssignmentAutomationData
                              .stageLeadAssignmentDefaultAutomation,
                      onlyUnassigned:
                          !stage.editableStageLeadAssignmentAutomationData
                              .stageLeadAssignmentAllowReassign,
                      specialCases: cleanSpecialCases(
                          stage.editableStageLeadAssignmentAutomationData
                              .stageLeadAssignmentAutomations
                      ),
                  }
                : {
                      defaultAutomation: {
                          assignee: null,
                          shouldRoundRobin: false,
                          shouldUnassign: false,
                      },
                      onlyUnassigned: true,
                      shouldRoundRobin: false,
                      specialCases: [],
                  };

            return (
                <SetUserAssignmentsMutation
                    mutation={SET_USER_ASSIGNMENT}
                    // @ts-expect-error
                    refetchQueries={refetchSalesAutomationOpenLeadQueries({
                        stageId: props.parentId,
                    })}
                    update={(store, mutationResult) => {
                        const setStageLeadAssignmentAutomation =
                            mutationResult.data &&
                            mutationResult.data.setStageLeadAssignmentAutomation;

                        // Read the data from our cache for this query.
                        const cacheResult: GetUserAssignmentsForStageQuery | null | undefined =
                            store.readQuery({
                                query: GET_USER_ASSIGNMENTS_FOR_STAGE,
                                variables: {stageId: props.parentId},
                            });

                        if (!cacheResult || !cacheResult.stage || !setStageLeadAssignmentAutomation)
                            return;

                        // Replace the editableStageLeadAssignmentAutomationData with new results
                        cacheResult.stage.editableStageLeadAssignmentAutomationData =
                            setStageLeadAssignmentAutomation.stage.editableStageLeadAssignmentAutomationData;

                        // Write our data back to the cache.
                        store.writeQuery({
                            query: GET_USER_ASSIGNMENTS_FOR_STAGE,
                            variables: {stageId: props.parentId},
                            data: cacheResult,
                        });
                    }}
                >
                    {(setUserAssignments) => {
                        return (
                            <SetUserAssignmentsMutation
                                mutation={SET_USER_ASSIGNMENT}
                                // @ts-expect-error
                                refetchQueries={refetchSalesAutomationOpenLeadQueries({
                                    stageId: props.parentId,
                                })}
                                update={(store, mutationResult) => {
                                    const setStageLeadAssignmentAutomation =
                                        mutationResult.data &&
                                        mutationResult.data.setStageLeadAssignmentAutomation;
                                    // Read the data from our cache for this query.
                                    const cacheResult:
                                        | GetUserAssignmentsForStageQuery
                                        | null
                                        | undefined = store.readQuery({
                                        query: GET_USER_ASSIGNMENTS_FOR_STAGE,
                                        variables: {stageId: props.parentId},
                                    });
                                    if (
                                        !cacheResult ||
                                        !cacheResult.stage ||
                                        !setStageLeadAssignmentAutomation
                                    )
                                        return;

                                    // Replace the stageLeadAssignmentAutomationData with new results
                                    cacheResult.stage.editableStageLeadAssignmentAutomationData =
                                        setStageLeadAssignmentAutomation.stage.editableStageLeadAssignmentAutomationData;

                                    // Write our data back to the cache.
                                    store.writeQuery({
                                        query: GET_USER_ASSIGNMENTS_FOR_STAGE,
                                        variables: {stageId: props.parentId},
                                        data: cacheResult,
                                    });
                                }}
                            >
                                {(deleteUserAssignments) => (
                                    <FormUserAssignmentsComponent
                                        initialValues={initialValues}
                                        entityId={props.entityId}
                                        assignables={props.assignables}
                                        schema={props.schema}
                                        formSubmitBtnRef={props.formSubmitBtnRef}
                                        onDelete={() =>
                                            deleteUserAssignments({
                                                variables: {
                                                    stageLeadAssignmentAutomation: {
                                                        stageId: props.parentId,
                                                        automationCases: [],
                                                    },
                                                },
                                            })
                                        }
                                        onSaveForm={(formData) =>
                                            setUserAssignments({
                                                variables: {
                                                    stageLeadAssignmentAutomation: {
                                                        stageId: props.parentId,
                                                        defaultAutomation:
                                                            getDefaultConfig(formData),
                                                        automationCases:
                                                            getAutomationCases(formData),
                                                        allowReassign: !formData.onlyUnassigned,
                                                    },
                                                },
                                            })
                                        }
                                    />
                                )}
                            </SetUserAssignmentsMutation>
                        );
                    }}
                </SetUserAssignmentsMutation>
            );
        }}
    </UserAssignmentsQuery>
);

// -----------------------------------------------------------------------------
//   Enhancer composition
// -----------------------------------------------------------------------------

export const FormUserAssignments = connector(FormUserAssignmentsGraphQL);
